import {
  assessmentSessionHasCapacityComplete,
  type AssessmentSessionRow,
} from "@/lib/api/assessments";
import type { EmployeeRow } from "@/types/database";

export function normEmail(s: string | null | undefined): string {
  return (s ?? "").trim().toLowerCase();
}

/** Resolve roster employee UUID for a session (linked id or email match). */
export function resolveSessionEmployeeId(
  s: AssessmentSessionRow,
  employees: EmployeeRow[]
): string | null {
  const db =
    s.employee_db_id != null && String(s.employee_db_id).trim() !== "" ? String(s.employee_db_id) : null;
  if (db && employees.some((e) => e.id === db)) return db;
  const match = employees.find((e) => normEmail(e.email) === normEmail(s.respondent_email));
  return match?.id ?? null;
}

/**
 * Sessions that belong to the given roster: linked `employee_db_id` or email match when id is missing.
 */
export function filterAssessmentSessionsForRoster(
  sessions: AssessmentSessionRow[],
  employees: EmployeeRow[]
): AssessmentSessionRow[] {
  if (employees.length === 0) return [];
  const idSet = new Set(employees.map((e) => e.id));
  const emailToId = new Map<string, string>();
  for (const e of employees) {
    emailToId.set(normEmail(e.email), e.id);
  }
  return sessions.filter((s) => {
    const db = s.employee_db_id != null && String(s.employee_db_id).trim() !== "" ? String(s.employee_db_id) : null;
    if (db && idSet.has(db)) return true;
    const rid = emailToId.get(normEmail(s.respondent_email));
    return rid != null;
  });
}

/**
 * Latest completed MAPS session per roster employee (by employee id).
 */
export function latestSessionByRosterEmployee(
  sessions: AssessmentSessionRow[],
  employees: EmployeeRow[]
): Map<string, AssessmentSessionRow> {
  const scoped = filterAssessmentSessionsForRoster(sessions, employees);
  const byEmp = new Map<string, AssessmentSessionRow[]>();
  for (const s of scoped) {
    let empId: string | null = null;
    if (s.employee_db_id != null && String(s.employee_db_id).trim() !== "") {
      empId = String(s.employee_db_id);
    } else {
      empId = employees.find((e) => normEmail(e.email) === normEmail(s.respondent_email))?.id ?? null;
    }
    if (!empId) continue;
    const arr = byEmp.get(empId) ?? [];
    arr.push(s);
    byEmp.set(empId, arr);
  }
  const out = new Map<string, AssessmentSessionRow>();
  for (const e of employees) {
    const arr = byEmp.get(e.id);
    if (!arr?.length) continue;
    const latest = arr.reduce((a, b) =>
      new Date(a.submitted_at).getTime() >= new Date(b.submitted_at).getTime() ? a : b
    );
    out.set(e.id, latest);
  }
  return out;
}

/**
 * Best session for testing wizard + Step-1 unlock: roster-scoped rows for `employeeId` (via `employee_db_id` or email),
 * newest first. Prefers the newest MAPS-complete submission so a stray incomplete row does not block unlock.
 */
export function primaryRosterAssessmentSessionForEmployee(
  sessions: AssessmentSessionRow[],
  employees: EmployeeRow[],
  employeeId: string
): AssessmentSessionRow | null {
  if (!employeeId.trim() || employees.length === 0) return null;
  const scoped = filterAssessmentSessionsForRoster(sessions, employees);
  const mine = scoped
    .filter((s) => resolveSessionEmployeeId(s, employees) === employeeId)
    .sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime());
  if (mine.length === 0) return null;
  const completed = mine.find((s) => assessmentSessionHasCapacityComplete(s));
  return completed ?? mine[0] ?? null;
}
